import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly ACCESS_TOKEN_EXPIRES_KEY = 'access_token_expires';
  private readonly REFRESH_TOKEN_EXPIRES_KEY = 'refresh_token_expires';

  /**
   * Guardar el access token con su fecha de expiración
   */
  setAccessToken(token: string, expires?: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
    if (expires) {
      localStorage.setItem(this.ACCESS_TOKEN_EXPIRES_KEY, expires);
    }
  }

  /**
   * Obtener el access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Guardar el refresh token con su fecha de expiración
   */
  setRefreshToken(token: string, expires?: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
    if (expires) {
      localStorage.setItem(this.REFRESH_TOKEN_EXPIRES_KEY, expires);
    }
  }

  /**
   * Obtener el refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Eliminar todos los tokens y sus fechas de expiración
   */
  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.ACCESS_TOKEN_EXPIRES_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_EXPIRES_KEY);
  }

  /**
   * Verificar si existe un access token válido (existe y no ha expirado)
   */
  hasValidAccessToken(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    const expiresStr = localStorage.getItem(this.ACCESS_TOKEN_EXPIRES_KEY);
    if (!expiresStr) return false; // Si no hay fecha de expiración, consideramos inválido por seguridad

    const expiresDate = new Date(expiresStr);

    // Validar que la fecha sea válida
    if (isNaN(expiresDate.getTime())) {
      return false;
    }

    const now = new Date();

    return now < expiresDate;
  }

  /**
   * Verificar si el refresh token es válido (existe y no ha expirado)
   */
  hasValidRefreshToken(): boolean {
    const token = this.getRefreshToken();
    if (!token) return false;

    const expiresStr = localStorage.getItem(this.REFRESH_TOKEN_EXPIRES_KEY);
    if (!expiresStr) return true; // Si no hay fecha guardada, asumir válido (la API lo validará)

    const expiresDate = new Date(expiresStr);

    // Validar que la fecha sea válida
    if (isNaN(expiresDate.getTime())) {
      return false;
    }

    const now = new Date();

    return now < expiresDate;
  }

  /**
   * Verificar si el access token está próximo a expirar (en los próximos 5 minutos)
   */
  isAccessTokenExpiringSoon(): boolean {
    const expiresStr = localStorage.getItem(this.ACCESS_TOKEN_EXPIRES_KEY);
    if (!expiresStr) return false;

    const expiresDate = new Date(expiresStr);

    // Validar que la fecha sea válida
    if (isNaN(expiresDate.getTime())) {
      return false;
    }

    const now = new Date();
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);

    return expiresDate <= fiveMinutesFromNow;
  }
}
